import React, {
  forwardRef,
  ForwardRefRenderFunction,
  MouseEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import Tippy from '@tippyjs/react'
import Modal from '@components/Modal/Modal'
import useKeypress from '@hooks/useKeyPress'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { Pages } from '@models/Pages'
import disabledLockIcon from '@assets/images/icons/lock-disabled.png'
import 'tippy.js/dist/tippy.css'

import closeIcon from '@assets/images/nav/close.png'
import closeHoverIcon from '@assets/images/nav/close-hover.png'
import gotchi from '@assets/images/characters/gotchi-shadow.png'
import facebookIcon from '@assets/images/icons/social/facebook.png'
import facebookHoverIcon from '@assets/images/icons/social/facebook-hover.png'
import instagramIcon from '@assets/images/icons/social/instagram.png'
import instagramHoverIcon from '@assets/images/icons/social/instagram-hover.png'
import shareIcon from '@assets/images/icons/social/share.png'
import twitterIcon from '@assets/images/icons/share/x.png'
import twitterHoverIcon from '@assets/images/icons/share/x-hover.png'
import chevronDownIcon from '@assets/images/icons/chevron-down-idle.png'
import chevronUpIcon from '@assets/images/icons/chevron-up-idle.png'
import { SurveyContext } from '@context/SurveyContext'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { DatagotchiBrandMark } from '@components/DatagotchiBrandMark/DatagotchiBrandMark'
import {
  getLayoutMode,
  isLayoutModeControlEnabled,
  setLayoutMode,
  type LayoutMode,
} from '../../lib/layoutMode'
import { isDesktop } from '@utils/platform'

interface OwnProps {
  onClose: (e?: MouseEvent<HTMLButtonElement> | MouseEvent) => void
}

export const MainMenu: ForwardRefRenderFunction<ReactNode, OwnProps> = ({ onClose }: OwnProps, ref) => {
  const { t } = useTranslation('MainMenu')
  const { step, enabled } = useContext(SurveyContext)
  const [selectedMenu, setSelectedMenu] = useState('')
  const location = useLocation()
  const [showChildren, setShowChildren] = useState(false)
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(() => getLayoutMode())
  useKeypress('Escape', onClose)
  const navigate = useNavigate()

  const isSandboxDisabled = !enabled || step !== SurveyStep.Sandbox
  const isAnswerDisabled =
    !enabled ||
    ![
      SurveyStep.Answer,
      SurveyStep.Email,
      SurveyStep.Acknowledge,
      SurveyStep.Prediction,
      SurveyStep.Rectify,
      SurveyStep.Forecasting,
      SurveyStep.Results,
    ].includes(step)
  const lockReason = (key: string): string | undefined => {
    if (key === 'sandbox' && isSandboxDisabled) {
      return !enabled ? t('lockReason.surveyDisabled') : t('lockReason.sandbox')
    }
    if (key === 'answer' && isAnswerDisabled) {
      return !enabled ? t('lockReason.surveyDisabled') : t('lockReason.answer')
    }
    return undefined
  }

  const links = [
    {
      key: 'home',
      link: `/${Pages.Home}`,
      end: true,
    },
    {
      key: 'sandbox',
      link: `/${Pages.Survey}/${SurveyStep.Sandbox}`,
      disabled: isSandboxDisabled,
      prefixIcon: isSandboxDisabled ? disabledLockIcon : undefined,
      overrideActiveClassName:
        location.pathname.includes(Pages.Survey) && location.pathname.includes(SurveyStep.Sandbox),
    },
    {
      key: 'answer',
      link: `/${Pages.Survey}/${SurveyStep.Answer}`,
      disabled: isAnswerDisabled,
      prefixIcon: isAnswerDisabled ? disabledLockIcon : undefined,
      overrideActiveClassName:
        location.pathname.includes(Pages.Survey) && !location.pathname.includes(SurveyStep.Sandbox),
    },
    {
      key: 'editions',
      link: `/${Pages.Editions}`,
      brandMark: true,
    },
    {
      key: 'informations',
      link: '',
      children: [
        {
          key: 'about',
          link: `/${Pages.About}`,
        },
        {
          key: 'methodology',
          link: `/${Pages.Methodology}`,
        },
        {
          key: 'partners',
          link: `/${Pages.Partners}`,
        },
        {
          key: 'terms',
          link: `/${Pages.Terms}`,
        },
        {
          key: 'privacy',
          link: `/${Pages.Privacy}`,
        },
        {
          key: 'contact',
          link: `/${Pages.Contact}`,
        },
      ],
    },
  ]

  const isNativeShareSupported = 'share' in navigator && typeof navigator.share === 'function'
  const onShare = () => {
    navigator.share({
      title: t('social.share.title'),
      url: location.pathname,
    })
  }

  const handleHeaderClose = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    onClose(e)
  }, [])

  const handleMenuClick = () => {
    setShowChildren((value) => !value)
  }

  const handleLayoutToggle = () => {
    const next: LayoutMode = layoutMode === 'app' ? 'wide' : 'app'
    setLayoutMode(next)
    setLayoutModeState(next)
  }

  const handleImageMouseEnterOrLeave = (e: MouseEvent, image: string) => {
    ;(e.target as HTMLImageElement).src = image
    return
  }

  const socialMedias = [
    {
      key: 'instagram',
      icon: instagramIcon,
      hoverIcon: instagramHoverIcon,
    },
    {
      key: 'facebook',
      icon: facebookIcon,
      hoverIcon: facebookHoverIcon,
    },
    {
      key: 'twitter',
      icon: twitterIcon,
      hoverIcon: twitterHoverIcon,
    },
  ]

  useEffect(() => {
    const [lastPartOfUrl] = location.pathname.split('/').reverse()
    if (lastPartOfUrl === 'index.html') {
      setSelectedMenu('home')
    }

    if (
      [
        `/${Pages.About}`,
        `/${Pages.Methodology}`,
        `/${Pages.Partners}`,
        `/${Pages.Privacy}`,
        `/${Pages.Terms}`,
        `/${Pages.Contact}`,
      ].includes(location.pathname)
    ) {
      setShowChildren(true)
    }
  }, [location])

  return (
    <Modal extraClass="main" ref={ref}>
      <div className="MainMenu">
        <Link to={`/${Pages.Home}`} className="MainMenu-header-link">
          <header className="MainMenu-header">
            <img src={gotchi} alt={t('title')} />

            <button onClick={handleHeaderClose}>
              <img
                src={closeIcon}
                alt={t('close')}
                onMouseOver={(e) => handleImageMouseEnterOrLeave(e, closeHoverIcon)}
                onMouseLeave={(e) => handleImageMouseEnterOrLeave(e, closeIcon)}
              />
            </button>
          </header>
          <div className="MainMenu-title">
            <h1>
              {t('title')}
              <small>{t('subTitle')}</small>
            </h1>
          </div>
        </Link>

        <nav className="MainMenu-body">
          {links.map(({ key, link, children, disabled, prefixIcon, overrideActiveClassName, brandMark }) => {
            const selectedClass = selectedMenu === key ? 'selected' : ''
            const disabledClass = disabled ? 'disabled' : ''
            const preAndSuFixClass = `${prefixIcon ? 'has-prefix' : ''} ${children?.length ? 'has-suffix' : ''}`
            return (
              <React.Fragment key={key}>
                {children ? (
                  <button
                    key={key}
                    className={clsx('MainMenu-body-link', {
                      active: showChildren,
                    })}
                    onClick={handleMenuClick}>
                    <h3>{t(`items.${key}`)}</h3>
                    <img className="suffix" src={showChildren ? chevronUpIcon : chevronDownIcon} />
                  </button>
                ) : disabled ? (
                  <Tippy
                    content={
                      <div className="MainMenu-lockTooltip">
                        <p className="MainMenu-lockTooltip-title">{t(`items.${key}`)}</p>
                        <p className="MainMenu-lockTooltip-body">
                          {lockReason(key) ?? t('lockReason.generic')}
                        </p>
                      </div>
                    }
                    placement="bottom-start"
                    trigger="mouseenter focus click"
                    touch={true}
                    interactive
                    delay={[80, 40]}
                    maxWidth={280}
                    // Modal shell is z-index 888 — tippy must sit above it when appendTo=body
                    zIndex={1200}
                    appendTo={() => document.body}>
                    <button
                      type="button"
                      className={clsx(
                        `MainMenu-body-link MainMenu-body-link--locked ${selectedClass} ${disabledClass} ${preAndSuFixClass}`,
                      )}
                      aria-disabled="true">
                      {prefixIcon && <img className="prefix" src={prefixIcon} alt="" />}
                      <h3>{t(`items.${key}`)}</h3>
                    </button>
                  </Tippy>
                ) : (
                  <NavLink
                    key={key}
                    to={link}
                    end
                    className={clsx(`MainMenu-body-link ${selectedClass} ${preAndSuFixClass}`, {
                      active: !!overrideActiveClassName,
                    })}>
                    {prefixIcon && <img className="prefix" src={prefixIcon} alt="" />}
                    <h3>
                      {brandMark ? <DatagotchiBrandMark label={t(`items.${key}`)} /> : t(`items.${key}`)}
                    </h3>
                  </NavLink>
                )}

                {children?.length &&
                  showChildren &&
                  children.map(({ key, link }) => {
                    const selectedClass = selectedMenu === key ? 'selected' : ''
                    return (
                      <button
                        key={key}
                        onClick={(e) => {
                          handleHeaderClose(e)
                          navigate(link)
                        }}
                        // to={link}
                        className={`Submenu-item ${selectedClass}`}>
                        {t(`items.${key}`)}
                      </button>
                    )
                  })}
              </React.Fragment>
            )
          })}
        </nav>

        {isDesktop && isLayoutModeControlEnabled() && (
          <div className="MainMenu-layoutToggle">
            <button type="button" className="MainMenu-body-link" onClick={handleLayoutToggle}>
              <h3>
                {layoutMode === 'app' ? t('items.layoutWide') : t('items.layoutApp')}
              </h3>
            </button>
          </div>
        )}

        <footer>
          {socialMedias.map(({ key, icon, hoverIcon }) => (
            <a
              key={key}
              title={t(`social.${key}.title`)}
              href={t(`social.${key}.link`)}
              target={key}
              rel="publisher noopener">
              <img
                src={icon}
                alt={t(`social.${key}.title`)}
                onMouseOver={(e) => handleImageMouseEnterOrLeave(e, hoverIcon)}
                onMouseLeave={(e) => handleImageMouseEnterOrLeave(e, icon)}
              />
            </a>
          ))}

          {isNativeShareSupported && (
            <button title={t('social.share.title')} onClick={onShare}>
              <img src={shareIcon} alt={t('social.share.title')} />
            </button>
          )}
        </footer>
      </div>
    </Modal>
  )
}

export default forwardRef(MainMenu)
